import type React from "react";

import { shouldBlockMapEntityCardOpen } from "@/features/add-device/should-block-map-entity-card-open";
import { getCardTypeFromEntity } from "@/features/cards/shared/card-routing/card-types";
import { openZustandCard } from "@/features/cards/shared/legacy/open-zustand-card";
import { getDeviceMapEntityById } from "@/services/session-manager/open-device-card-by-id";

import FocusContext from "./focus-context";
import type MapEntityBase from "./map-entities/map-entity-base";

/**
 * What a click on a marker does, and the persistent cards that refuse to be usurped by one.
 *
 * Lives beside `FocusContext` because the gate reads it, and both marker layers import it directly
 * rather than being handed it — a marker layer importing `FocusContext` is the coupling this file
 * contains. Retires with `FocusContext`; the reads here protect Add-Device and Scheduler.
 */

/** Whether a card that is never usurped automatically currently owns the screen. */
export const isPersistentCardOpen = () =>
    shouldBlockMapEntityCardOpen(FocusContext.currentInfoCard?.currentInfoCardType);

/** Opens a map entity's own card, unless a persistent card owns the screen. */
export const openCardForMapEntity = (mapEntity: MapEntityBase) => {
    if (isPersistentCardOpen()) {
        return;
    }
    openZustandCard({ entity: mapEntity, cardType: getCardTypeFromEntity(mapEntity) });
};

/**
 * The same click from a v2-drawn marker, which has no MapEntity of its own to pass.
 *
 * Does nothing when the document reached the map before SessionHandler built the entity — there is no
 * card to show yet.
 */
export const openCardForDeviceId = (deviceId: string) => {
    const mapEntity = getDeviceMapEntityById(deviceId);
    if (mapEntity) {
        openCardForMapEntity(mapEntity);
    }
};

/**
 * The same click from inside a group's menu.
 *
 * Differs from `openCardForMapEntity` only while a card is blocking: a member row offers the click to
 * the active flow through `FocusContext.onInteraction`, where an individual marker ignores it. The
 * observable effect is the Scheduler — a device can be picked for a schedule from a group menu but not
 * from its own marker.
 *
 * Intentionally not collapsed into `openCardForMapEntity`: the two differ in whether a blocked click
 * is offered onwards, which is a behaviour the Scheduler depends on. Add-Device is unaffected either
 * way, since its focus context's `onInteract` returns false and halts the walk before anything claims
 * the click.
 */
export const openCardForGroupMember = (event: React.MouseEvent<HTMLElement>, mapEntity: MapEntityBase) => {
    if (isPersistentCardOpen()) {
        FocusContext.onInteraction(event, mapEntity);
        return;
    }
    openZustandCard({ entity: mapEntity, cardType: getCardTypeFromEntity(mapEntity) });
};
